import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button, useLogin } from 'react-admin'
import { ENV_BASED_CONSTANTS } from '../../utils/constants'

// Survives the sign-in round-trip through Shopify, which React state cannot.
// authProvider.logout() removes its own keys individually rather than clearing
// storage, so a stashed redirect is not wiped by the requireAuth bounce.
const REDIRECT_KEY = 'provider_portal_post_login_redirect'

// Where to land after a successful token login.
//
// Digest emails link straight at a page and carry no credential: a reader with a
// live session lands there directly, and one without is bounced here with the
// intended path in location state.
//
// Only same-origin relative paths are honoured. Accepting an absolute or
// protocol-relative URL would turn a trusted MaleFromHome link into an open
// redirect.
const safeRedirect = (value) => {
  if (!value) return null
  if (!value.startsWith('/')) return null
  // '//host' and '/\host' are protocol-relative and leave the origin.
  if (value.startsWith('//') || value.startsWith('/\\')) return null
  if (value.startsWith('/login')) return null
  return value
}

const readStashedRedirect = () => {
  try {
    return safeRedirect(localStorage.getItem(REDIRECT_KEY))
  } catch (e) {
    return null
  }
}

const stashRedirect = (value) => {
  const safe = safeRedirect(value)
  if (!safe) return
  try {
    localStorage.setItem(REDIRECT_KEY, safe)
  } catch (e) {
    // Private browsing or blocked storage: the deep link is lost, but sign-in
    // still works and lands on the dashboard.
  }
}

const clearStashedRedirect = () => {
  try {
    localStorage.removeItem(REDIRECT_KEY)
  } catch (e) {
    /* nothing to do */
  }
}

const TokenVerifier = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const login = useLogin()
  const [status, setStatus] = useState('verifying') // 'verifying', 'error', 'no-token'

  // TODO: https://malefromhome.com/pages/provider-enrollment, add a redirect to this page

  useEffect(() => {
    const verifyToken = async () => {
      const params = new URLSearchParams(location.search)
      const token = params.get('token')
      const state = params.get('state')

      // Capture where they were headed before anything else, so it is stored
      // even on the branches that return early below.
      //   - location.state.nextPathname: set by requireAuth's logout bounce
      //     when an email link hit a page they were not signed in for
      //   - ?redirect=: an explicitly constructed deep link
      const intended = location.state && location.state.nextPathname
      const intendedSearch = (location.state && location.state.nextSearch) || ''
      if (intended) {
        stashRedirect(intended + intendedSearch)
      }
      stashRedirect(params.get('redirect'))

      if (state === 'logout') {
        // A real sign-out clears any pending deep link; being bounced here by an
        // email click does not, which is what distinguishes the two messages.
        setStatus(readStashedRedirect() ? 'no-token' : 'logout')
        return
      }

      if (!token) {
        setStatus('no-token')
        return
      }

      try {
        await login(token)
        setStatus('success')

        const stashed = readStashedRedirect()
        clearStashedRedirect()
        navigate(stashed || '/')
      } catch (error) {
        setStatus('error')
      }
    }

    verifyToken()
  }, [location, login, navigate])

  return (
    <div className="verify-token-container">
      <img src={'/Male-from-Home-WITHOUT-SHADOW-2.png'} alt="MFH Logo" />
      <h2>MaleFromHome Provider Portal</h2>
      {status === 'verifying' && <div>Verifying access token...</div>}
      {(status === 'no-token' || status === 'logout') && (
        <div>
          {status === 'no-token' ? (
            <p style={{ fontWeight: 'bold', marginBottom: '20px' }}>
              <span style={{ color: 'red' }}>No access token found.</span> If you don't have access to the portal, please click
              signup below to enroll as a provider. You will be contacted by our team to verify your information and provide access
              to the portal.
            </p>
          ) : (
            <p style={{ fontWeight: 'bold', marginBottom: '20px' }}>You have been logged out.</p>
          )}

          <div className="verify-token-signin">
            <Button
              variant="contained"
              size="large"
              component="a"
              href={ENV_BASED_CONSTANTS.mfhAccount}
              style={{ textDecoration: 'none' }}
            >
              Sign In
            </Button>
          </div>

          <div className="verify-token-signup">
            {/* status === 'no-token' */ true && (
              <Button
                variant="contained"
                size="large"
                onClick={() => window.open(ENV_BASED_CONSTANTS.providerEnrollment)}
              >
                Sign Up
              </Button>
            )}
          </div>
        </div>
      )}
      {status === 'error' && (
        <div>
          <p style={{ color: 'red', fontWeight: 'bold', marginBottom: '20px' }}>
            Invalid or expired access token. Please contact support for assistance or sign in again.
          </p>
          <a href={ENV_BASED_CONSTANTS.mfhAccount} style={{ textDecoration: 'none' }}>
            <Button
              variant="contained"
              size="large"
              label="Sign In"
            />
          </a>
        </div>
      )}
      {status === 'success' && (
        <div style={{ color: 'green' }}>
          <p>Successfully authenticated!</p>
        </div>
      )}
    </div>
  )
}

export default TokenVerifier
